import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { FlashMessage } from '@/hooks/useFlashMessage';

/**
 * The strip under the lookup bar that says how the last action went.
 *
 * All eight master screens carried this markup. Seven agreed on it and the cost centre screen had
 * drifted to a tighter bar with smaller text and a smaller icon, so the same sentence about the
 * same kind of event was set two different ways depending on which master you happened to be on.
 * The seven win on size; the cost centre screen was right about `flex-shrink-0`, which keeps the
 * bar from being squeezed out of a full-height flex column, so that is kept.
 *
 * Renders nothing when there is no message, so callers need no conditional of their own.
 */
export const MasterFlash = ({ message }: { message: FlashMessage | null }) => {
  if (!message) return null;

  const succeeded = message.type === 'success';

  return (
    <div
      role="status"
      className={cn(
        'flex-shrink-0 flex items-center gap-2 px-5 py-2.5 text-sm',
        succeeded
          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-b border-emerald-200 dark:border-emerald-800'
          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800',
      )}
    >
      {succeeded
        ? <CheckCircle2 size={15} className="flex-shrink-0" />
        : <AlertTriangle size={15} className="flex-shrink-0" />}
      {message.text}
    </div>
  );
};
